import { Injectable } from '@angular/core';
import { ApiService } from '../../../shared/services/api/api.service';
import {
  CoreLogTypeEnum,
  LogData,
} from '../../../shared/interfaces/core-logs.interface';
import {
  CaseDocumentSavedTypeEnum,
  CaseGetList,
  CaseRecordTypeEnum,
  CaseStatusesEnum,
} from '../interfaces/interfaces';
import { IPagination } from '../../../shared/services/api/interface/pagination.interface';
import {
  CasePaymentReceiver,
  ICasePayment,
  ICaseStatusHistory,
  ICaseStatusLogs,
  ICaseVehicleDuration,
  INewCasePayment,
} from '../components/case-card/interfaces/case-card-interfaces';
import { Observable } from 'rxjs';
import { ICaseFeatureInformations } from '../components/case-card/components/tab-header-components/case-feature-informations/case-feature-informations.interface';
import { ICaseUserTimeline } from '../components/case-vehicle-timeline/case-vehicle-timeline.component';

@Injectable({
  providedIn: 'root',
})
export class CaseService {
  constructor(private readonly apiService: ApiService) {}

  getLogs(sourceId: string, type: string = CoreLogTypeEnum.case) {
    return this.apiService.get<LogData>('cases/get-logs', {
      sourceId,
      type,
    });
  }
  getList(filter: CaseGetList) {
    return this.apiService.post<IPagination>('cases/get-case-list', filter);
  }
  getCaseListByIntituationId(
    id: number | string,
    filters: {
      caseType: number | null;
      caseStatus: number | null;
      startDate: Date | null;
      endDate: Date | null;
      callInstitution: boolean | null;
      dropoffInstitution: boolean | null;
      pickupInstitution: boolean | null;
      first: number;
      rows: number;
      sortField: any;
      sortOrder: any;
    }
  ) {
    return this.apiService.post<{ total: number; items: any[] }>(
      `cases/by-instituation-id/${id}`,
      filters
    );
  }
  getCaseListByPatientId(
    id: number | string,
    filters: {
      caseType: number | null;
      caseStatus: number | null;
      startDate: Date | null;
      endDate: Date | null;
      callingInstitution: number | null;
      dropoffInstitution: number | null;
      pickupInstitution: number | null;
      first: number;
      rows: number;
      sortField: any;
      sortOrder: any;
    }
  ) {
    return this.apiService.post<{
      total: number;
      items: any[];
      analize: any[];
    }>(`cases/by-patient-id/${id}`, filters);
  }

  sendSms(content: string, phones: string[], extraDatas: {}) {
    return this.apiService.post<any>(`cases/send-sms`, {
      content,
      phones,
      extraDatas,
    });
  }

  sendEmail(formData: FormData, caseId?: number) {
    return this.apiService.post<any>(`cases/send-email?caseId=${caseId}`, formData);
  }

  caseComplete(caseId: number) {
    return this.apiService.get<{
      isValid: boolean;
      validationErrors: string[];
    }>(`cases/case-complete/${caseId}`);
  }

  cancelCase(data: {
    cancelReason: number;
    cancelDescription: string;
    caseId: number;
  }) {
    return this.apiService.post<{ isSuccess: boolean; message: string }>(
      `cases/cancel-case`,
      data
    );
  }

  approveCase(caseId: number) {
    return this.apiService.get<{
      isValid: boolean;
      validationErrors: string[];
    }>(`cases/approve-case/${caseId}`);
  }

  unapproveCase(caseId: number) {
    return this.apiService.get<{ isSuccess: boolean }>(
      `cases/unapprove-case/${caseId}`
    );
  }

  getAppointmentList(filter: any) {
    return this.apiService.post<IPagination>(
      'cases/get-appointment-list',
      filter
    );
  }

  saveDocument(formData: FormData) {
    return this.apiService.post<{ success: boolean; data: any }>(
      `cases/save-document`,
      formData
    );
  }

  deleteDocument(data: { caseId: number; documentId: number }) {
    return this.apiService.post<{ success: boolean; data: any }>(
      `cases/delete-document`,
      data
    );
  }

  getDocuments(
    caseId: number,
    filter: { savedType?: CaseDocumentSavedTypeEnum }
  ) {
    return this.apiService.post<{ success: boolean; documents: any }>(
      `cases/get-documents`,
      { caseId, filter }
    );
  }

  getVehiclesForCase(caseId: number) {
    return this.apiService.get<{ success: boolean; vehiclesList: any[] }>(
      `vehicle/get-vehicles-for-case/${caseId}`
    );
  }

  assignVehicle(caseId: number, vehicleId: number, isMainVehicle: boolean) {
    return this.apiService.post<{ success: boolean; response: any }>(
      `cases/assign-vehicle`,
      {
        caseId,
        vehicleId,
        isMainVehicle,
      }
    );
  }

  setCasePayment(
    caseId: number | null,
    caseType: number | null,
    payment: INewCasePayment
  ) {
    return this.apiService.post<{
      success: boolean;
      response: {
        caseDate: Date;
        caseNo: number;
        caseStatus: CaseStatusesEnum;
        caseType: any;
        id: number;
        paymentInformations: ICasePayment[];
        createdAt: Date;
        recordType: CaseRecordTypeEnum;
        uid: string;
      };
    }>(`cases/set-payment-information`, {
      caseId,
      caseType,
      ...payment,
    });
  }

  deleteCasePayment(caseId: number, paymentId: number) {
    return this.apiService.post<{
      success: boolean;
    }>(`cases/delete-payment-information`, {
      caseId,
      paymentId,
    });
  }

  getStatusLogsByCase(caseId: number) {
    return this.apiService.get<any[]>(
      `mobile-status/get-status-logs/${caseId}`
    );
  }

  getStatusFirstVehicleLogsByCase(caseId: number) {
    return this.apiService.get<{
      caseLogs: ICaseStatusLogs;
      caseDurations: ICaseVehicleDuration;
      userLogs: ICaseUserTimeline[];
    }>(`mobile-status/get-status-first-vehicle-logs-by-case-id/${caseId}`);
  }

  setVehicleLogManuellyByCase(caseId: number, dates: {
    callDate?: Date;
    ongoingDate?: Date;
    arriveDate?: Date;
    caseEndDate?: Date;
  }) {
    return this.apiService.patch<{
      success: boolean;
      response: ICaseStatusHistory;
    }>(`mobile-status/set-vehicle-log-manually-by-case-id`, {
      caseId,
      ...dates
    });
  }

  updateStartEndDateLog(id: number, startDate: Date, endDate: Date) {
    return this.apiService.patch<{
      success: boolean;
      response: ICaseStatusHistory;
    }>(`mobile-status/update-start-end-date`, {
      id,
      startDate,
      endDate,
    });
  }

  sendCerebral(caseId: number) {
    return this.apiService.post<{
      isSuccess: boolean;
    }>(`cerebral/send`, {
      caseId,
    });
  }

  getCerebralLogsByCaseId(caseId: number) {
    return this.apiService.get<any>(`cerebral/get-cerebral-logs/${caseId}`);
  }

  copyCase(caseId: number): Observable<{ success: true; caseId: number }> {
    return this.apiService.post<{ success: true; caseId: number }>(
      'cases/copy-case',
      { caseId }
    );
  }

  getCaseFeatures(caseId: number) {
    return this.apiService.get<{
      success: true;
      items: ICaseFeatureInformations[];
    }>(`cases/get-case-features/${caseId}`);
  }

  setCaseFeatures(
    caseId: number,
    caseFeatures: { id?: number; featureId: number; answer: string }[]
  ) {
    return this.apiService.post<{
      success: true;
      items: ICaseFeatureInformations[];
    }>(`cases/set-case-features/`, {
      caseId,
      caseFeatures,
    });
  }

  caseFollowUp(caseId: number) {
    return this.apiService.get<any>(`case-follow-up/case/${caseId}`);
  }

  getCasePaymentReceiver(caseId: number) {
    return this.apiService.get<{
      success: boolean;
      item: CasePaymentReceiver;
    }>(`cases/get-case-payment-receiver/${caseId}`);
  }
  deleteCasePaymentReceiver(caseId: number) {
    return this.apiService.delete<{
      success: boolean;
    }>(`cases/delete-case-payment-receiver/${caseId}`);
  }

  setPaymentReceiverForCase(data: CasePaymentReceiver, caseId: number) {
    return this.apiService.post<{ success: true }>(
      'cases/set-case-payment-receiver',
      { ...data, caseId: caseId }
    );
  }

  hasAssignedVehicleBefore(caseId: number) {
    return this.apiService.get<{
      success: boolean;
      hasAssignedBefore: boolean;
    }>(`cases/has-assigned-vehicle-before/${caseId}`);
  }
  caseConvertToAppointment(
    caseId: number,
    dateObj: {
      appointmentStartDate: Date;
      appointmentEndDate: Date;
      appointmentDuration: string;
    }
  ) {
    return this.apiService.patch<{
      success: boolean;
    }>(`cases/case-convert-to-appointment`, {
      caseId,
      appointmentStartDate: dateObj.appointmentStartDate,
      appointmentEndDate: dateObj.appointmentEndDate,
      appointmentDuration: dateObj.appointmentDuration,
    });
  }

  getCaseById(caseId: number) {
    return this.apiService.get(`cases/${caseId}`);
  }

  setCaseInformations(payload: any): Observable<any> {
    return this.apiService.post(`cases/set-case-informations`, payload);
  }

  caseAssignUser(payload: any){
    return this.apiService.post(`cases/assign-user`, payload);
  }

 takeOverCase(payload: {
  caseId: string;
  newEditor: string;
  oldEditor: string | null;
}) {
  return this.apiService.post(
    'cases/take-over',
    payload
  );
}

  getCaseAssignUserLogs(caseId: number) {
    return this.apiService.post<any[]>(
      'cases/assign-user-logs',
      { caseId }
    );
  }


  getCerebralPatientInformations(
    patientId: number | null | undefined,
    caseId: number | null | undefined,
  ) {
    return this.apiService.post<any>(
      'cerebral/get-cerebral-patient-logs',
      {
        patientId: patientId ?? null,
        caseId: caseId ?? null,
      },
    );
  }

  approveAllCase(filter: { ids: number[] }) {
    return this.apiService.post<any>('cases/approve-all-case', filter);
  }

  unapproveAllCase(filter: { ids: number[] }) {
    return this.apiService.post<any>('cases/unapprove-all-case', filter);
  }
}
